import { computed, ref } from "vue";
import { acceptHMRUpdate, defineStore, storeToRefs } from "pinia";

import * as tmi from "@/constants";

import { useArticlesStore } from "@/stores/Articles";
import { useAuthStore } from "@/stores/Auth";
import { useAuthorsStore } from "@/stores/Authors";
import { useBonusReportsStore } from "@/stores/BonusReports";
import { useCategoriesStore } from "@/stores/Categories";
import { useCoursesStore } from "@/stores/Courses";
import { useIndustrySectorsStore } from "@/stores/IndustrySectors";
import { useModelPortfoliosStore } from "@/stores/ModelPortfolios";
import { useMyPortfoliosStore } from "@/stores/MyPortfolios";
import { usePermissionsStore } from "@/stores/Permissions";
import { useProductsStore } from "@/stores/Products";
import { useSectorsStore } from "@/stores/Sectors";
import { useSecuritiesStore } from "@/stores/Security";
import { useSimplePagesStore } from "@/stores/SimplePages";
import { useTagsStore } from "@/stores/Tags";
import { useTickersStore } from "@/stores/Tickers";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useUserStore } from "@/stores/User";
import { useWebinarsStore } from "@/stores/Webinars";

import { ContentfulItem, HasAuthor } from "@/types/entities";

import { isNewVersionAvailable } from "@/services/version-service";
import { FiveMinutesInMilliseconds } from "@/constants";

import routeManager from "@/router/route-manager";
import type { Credentials } from "@/types/authentication";
import {
    articleKey,
    authorsSyncKey,
    bonusReportKey,
    categoriesSyncKey,
    industrySectorsSyncKey,
    productsSyncKey,
    sectorAlertsKey,
    sectorsSyncKey,
    simplePagesSyncKey,
    tagsSyncKey,
    tickersSyncKey,
    tradeAlertKey,
    webinarKey
} from "@/types/products/product-definitions";

import { RouteLocationNormalizedLoaded, useRouter } from "vue-router";

// UTILITIES
import { useSectorHistoryStore } from "@/stores/SectorHistory";
import { createDataSyncEvent, type DataSyncEvent } from "@/utils/data-sync-event-factory";
import {
    buildStandardHeaders,
    defaultReasonForAbortingApiCall,
    goFetch,
    isSuccessState,
    normalizeFetchParameters
} from "@/utils/network-utils";
import { reportContentViewEvent } from "@/composables/user-activity-monitor";

import { Forbidden, InternalError, NotFound } from "@/types/api/responses";
import { MessageTypes } from "@/types/enums/message-types";

import type { CompositionContext } from "@/services/data/sync-supervisor";
import type { RestfulPayload, SignUpPayload, LoginPayload } from "@/types/api/fetch";
import { useLocalStorage } from "@vueuse/core";
import { resetCurrentCachedData } from "@/plugins/LocalForagePlugin";

import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

const worker: Worker = new Worker( new URL( '@/services/data/sync-worker.ts?worker', import.meta.url ), { type: 'module' } );

const MaintenanceRefreshInterval: number = FiveMinutesInMilliseconds;

export const useApplicationStore = defineStore( 'application', () => {
    // Global references
    const router = useRouter();

    // Store references - used to clear state upon logout
    const articlesStore = useArticlesStore();
    const authStore = useAuthStore();
    const authorsStore = useAuthorsStore();
    const bonusReportsStore = useBonusReportsStore();
    const categoriesStore = useCategoriesStore();
    const coursesStore = useCoursesStore();
    const industrySectorsStore = useIndustrySectorsStore();
    const modelPortfoliosStore = useModelPortfoliosStore();
    const myPortfoliosStore = useMyPortfoliosStore();
    const permissionsStore = usePermissionsStore();
    const productsStore = useProductsStore();
    const sectorsStore = useSectorsStore();
    const sectorHistoryStore = useSectorHistoryStore();
    const securitiesStore = useSecuritiesStore();
    const simplePagesStore = useSimplePagesStore();
    const tagsStore = useTagsStore();
    const tickersStore = useTickersStore();
    const tradeAlertsStore = useTradeAlertsStore();
    const webinarsStore = useWebinarsStore();
    const userStore = useUserStore();

    /// STATE
    const { isAuthenticated } = storeToRefs( authStore );
    const adminSession = ref< boolean >( false );
    const authenticating = ref< boolean >( false );

    const initializing = ref< boolean >( false );
    const publicInitialized = ref< boolean >( false );
    const privateInitialized = ref< boolean >( false );
    const loading = ref< boolean >( false );
    const isLoading = computed< boolean >( () => loading.value );

    const contentModalVisible = ref< boolean >( false );
    const contentItem = ref< HasAuthor | ContentfulItem | undefined >();
    const contentKey = ref< string | undefined >();

    const { isLoading: modelPortfoliosLoading, initialized: modelPortfolioInitialized } = storeToRefs( modelPortfoliosStore );

    const apiStatus = ref< number | undefined >();
    const isForbidden = computed< boolean >( () => apiStatus.value === Forbidden.statusCode );
    const isInternalError = computed< boolean >( () => apiStatus.value === InternalError.statusCode );
    const isNotFound = computed< boolean >( () => apiStatus.value === NotFound.statusCode );

    const dateOfMostRecentMarketDataAvailable = useLocalStorage< string >( 'latest-dt', '' );

    const compositionCtx: CompositionContext = {
        getAuthorById: ( id: string ) => authorsStore.authorById( id ),
        getCategoryById: ( id: string ) => categoriesStore.categoryById( id ),
        getIndustrySectorById: ( id: string ) => industrySectorsStore.industrySectorById( id ),
        getProductById: ( id: string ) => productsStore.productById( id ),
        getSectorById: ( id: string ) => sectorsStore.sectorById( id ),
        getSectorByTmiId: ( id: number ) => sectorsStore.sectorByTmiId( id ),
        getTagById: ( id: string ) => tagsStore.tagById( id ),
        getTickerById: ( id: string ) => tickersStore.tickerById( id )
    };

    const supportedModelPortfoliosActions: Array< string > = [
        'switchPortfolioContext'
    ];
    modelPortfoliosStore.$onAction( async ( { name } ): Promise< void > => {
        // Rules:
        //  - must be logged in
        //  - ModelPortfolioStore must not be initialized (sliding window)
        //  - ModelPortfolioStore must not be in the process of loading
        //  - the action being intercepted must be in the list of supported actions
        if ( !isAuthenticated.value || modelPortfolioInitialized.value || modelPortfoliosLoading.value || !supportedModelPortfoliosActions.includes( name ) ) {
            return;
        }

        await modelPortfoliosStore.initializeAsync();
    } );

    /**
     * This call is not authenticated and thus does not need to first validate that the session is valid
     */
    async function refreshDateOfMostRecentMarketDataAvailable() {
        try {
            const fetchParams = normalizeFetchParameters( {
                endpoint: `${ import.meta.env.VITE_SENTIENT_REL_PATH }`,
                payload: {
                    query: `{ dateOfLatestMarketDataAvailable { latest_mk_dt } }`
                }
            } );

            const headers = buildStandardHeaders();

            // Create an AbortController to short-circuit an API call that is taking too long
            const monitor = new AbortController();
            const timeoutId = setTimeout( () => monitor.abort( defaultReasonForAbortingApiCall ), fetchParams.maxApiDuration );

            const init = {
                method: fetchParams.method,
                body: fetchParams.body,
                headers: headers,
                signal: monitor.signal
            };

            // Perform the API call using fetch or any other suitable method
            const response: Response = await fetch( fetchParams.uri, init );


            // As a generally good practice, always clean up any resources that may or may not be active
            clearTimeout( timeoutId );

            if ( response.ok ) {
                let responsePayload: any = {};
                try {
                    responsePayload = await response.json();
                } catch ( err: any ) { /* IGNORE */
                }

                if ( responsePayload?.data?.dateOfLatestMarketDataAvailable?.latest_mk_dt ) {
                    dateOfMostRecentMarketDataAvailable.value = responsePayload.data.dateOfLatestMarketDataAvailable.latest_mk_dt;
                }
            }
        } catch ( err: any ) {
            /* IGNORE */
        }
    }

    async function initializeAsync(): Promise< void > {
        if ( initializing.value ) {
            return;
        }

        try {
            initializing.value = true;
            if ( !publicInitialized.value ) {
                await Promise.allSettled( [
                    refreshDateOfMostRecentMarketDataAvailable(),
                    productsStore.initializeAsync(),
                    authorsStore.initialize(),
                    categoriesStore.initialize(),
                    industrySectorsStore.initialize(),
                    simplePagesStore.initializeAsync(),
                    sectorsStore.initializeAsync(),
                    tagsStore.initialize(),
                    tickersStore.initialize()
                ] );

                publicInitialized.value = true;
            }

            // Required to validate other authorized stores
            if ( !privateInitialized.value && isAuthenticated.value ) {
                if ( !permissionsStore.isInitialized ) {
                    permissionsStore.initializeSynchronously();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.ArticlesPermissionKey ) ) {
                    articlesStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.BonusReportsPermissionKey ) ) {
                    bonusReportsStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.SectorAlertsPermissionKey ) ) {
                    await sectorHistoryStore.initializeAsync();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.ProductTradeAlertsPermissionKey ) ) {
                    tradeAlertsStore.initialize();
                }

                if ( permissionsStore.hasModelPortfolioAccess ) {
                    await modelPortfoliosStore.initializeAsync();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.WebinarsPermissionsKey ) ) {
                    webinarsStore.initialize();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.PortfolioBuilderPermissionKey ) ) {
                    myPortfoliosStore.initialize().then();
                }

                if ( permissionsStore.hasFeatureAccess( tmi.CoursePermissionsKey ) ) {
                    await coursesStore.initializeAsync();
                }

                privateInitialized.value = true;
            }

            fireDataSyncEvent();
        } catch ( err: any ) {
            /* IGNORE */
        } finally {
            initializing.value = false;
        }
    }

    function setSelectedArticle( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = articleKey;
            contentItem.value = articlesStore.articleById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedBonusReport( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = bonusReportKey;
            contentItem.value = bonusReportsStore.bonusReportById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedTradeAlert( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = tradeAlertKey;
            contentItem.value = tradeAlertsStore.tradeAlertById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function setSelectedWebinar( id: string | undefined ): void {
        if ( id ) {
            contentKey.value = webinarKey;
            contentItem.value = webinarsStore.webinarById( id );
            contentModalVisible.value = true;
            reportContentViewEvent( contentItem.value, contentKey.value );
        } else {
            contentKey.value = undefined;
            contentItem.value = undefined;
            contentModalVisible.value = false;
        }
    }

    function clearSelectedContent() {
        setSelectedArticle( undefined );
        setSelectedTradeAlert( undefined );
        setSelectedWebinar( undefined );
    }

    /***********************************************************************************************
     AUTHENTICATION API
     ***********************************************************************************************/
    async function signUp( signUpInfo: SignUpPayload ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            const response = await goFetch< void, RestfulPayload >( {
                endpoint: `registrar/freemium/enroll`,
                payload: signUpInfo
            } );

            if ( response && isSuccessState( response.status ) ) {
                const credentials : LoginPayload = {
                    username: signUpInfo.email,
                    password: signUpInfo.password
                };

                await signIn( credentials );
            }
        } catch ( err: any ) {
            await resetApplication(true, true);

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }
    }

    async function signIn( credentials: Credentials ): Promise< void > {
        try {
            // Set the loading state for the given store
            loading.value = true;

            // Attempt to load the initial state for the store
            await authenticate( credentials );

            // Refresh all stores
            await initializeAsync();
        } catch ( err: any ) {
            await resetApplication(true, true);

            throw err;
        } finally {
            // Processing is finished - successfully or unsuccessfully
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            forwardUserToInitialPath();
        }
    }

    function forwardUserToInitialPath(): void {
        routeManager.handleRedirectWithOptionalExplicitPath( router );
    }

    async function authenticate( credentials: Credentials ): Promise< void > {
        if ( isAuthenticated.value || authenticating.value ) {
            logger.info( `authenticating.value: ${ authenticating.value } || authenticated.value: ${ isAuthenticated.value }...returning` );
            return;
        }

        // Always clear the admin session variable (it is set when the authentication path is via the token assignment)
        adminSession.value = false;

        loading.value = true;
        authenticating.value = true;

        logger.debug( 'authenticating' );

        await authStore.authenticate( credentials );
        logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

        if ( isAuthenticated.value ) {
            await userStore.initializeAsync();
            await permissionsStore.initializeAsync();
        }
    }

    async function authenticateWithToken( authToken: string ) : Promise< void > {
        // Validate the JWT before doing anything
        const isValidJwt = authStore.isValidJwt( authToken );
        if ( !isValidJwt ) {
            logger.error( 'Invalid JWT token provided' );
            return;
        }

        // Assign the new token FIRST before clearing anything
        // This prevents route guards from seeing an unauthenticated state
        authStore.assignToken( authToken );

        // Reset all store state (but token is already set above)
        adminSession.value = false;
        authenticating.value = false;
        initializing.value = false;
        publicInitialized.value = false;
        privateInitialized.value = false;

        articlesStore.reset();
        authorsStore.reset();
        bonusReportsStore.reset();
        categoriesStore.reset();
        coursesStore.reset();
        industrySectorsStore.reset();
        modelPortfoliosStore.reset();
        myPortfoliosStore.reset();
        permissionsStore.reset();
        productsStore.reset();
        sectorHistoryStore.reset();
        sectorsStore.reset();
        securitiesStore.reset();
        simplePagesStore.reset();
        tagsStore.reset();
        tickersStore.reset();
        tradeAlertsStore.reset();
        webinarsStore.reset();
        userStore.reset();

        try {
            loading.value = true;
            logger.debug( `authenticate: success: ${ isAuthenticated.value }` );

            if ( isAuthenticated.value ) {
                await userStore.initializeAsync();
                await permissionsStore.initializeAsync();

                // Initialize all stores (products, sectors, etc.) just like signIn does
                await initializeAsync();
            }
        } catch ( err ) {
            await resetApplication( true, true );

            throw err;
        } finally {
            authenticating.value = false;
            loading.value = false;
        }

        // If there are no errors (e.g.; the store has been initialized)
        // forward the user to the initial page
        if ( isAuthenticated.value ) {
            adminSession.value = true;
            forwardUserToInitialPath();
        }
    }

    function fireDataSyncEvent() {
        // Fire off the sync event loop
        if ( !!worker ) {
            if ( isAuthenticated.value ) {
                worker.postMessage( createDataSyncEvent( MessageTypes.REQUEST_SECURE_SYNC, permissionsStore.authorizationGrants() ) );
            }
        }
    }

    if ( !!window ) {
        window.onmessage = async function ( msg: MessageEvent< DataSyncEvent > ): Promise< void > {
            try {
                const data = msg?.data;
                if ( data && data?.typeSignifier ) {
                    await handleErrorsAsync( msg.data.eventType, msg.data.payload );
                }
            } catch ( err: any ) {
                logger.error( `Issue posted to internal channel: ${ JSON.stringify( err?.message ) }` );
            }
        }
    }

    if ( !!worker ) {
        worker.onmessage = async function ( msg: MessageEvent< DataSyncEvent > ): Promise< void > {
            const messageType: MessageTypes = msg.data.eventType;
            const payload = msg.data.payload;
            if ( MessageTypes.UPDATE_COMPLETE === messageType ) {
                if ( payload ) {
                    switch ( payload ) {
                        case articleKey:
                            articlesStore.reset();
                            articlesStore.initialize();
                            break;
                        case tradeAlertKey:
                            tradeAlertsStore.reset();
                            tradeAlertsStore.initialize();
                            break;
                        case webinarKey:
                            webinarsStore.reset();
                            webinarsStore.initialize();
                            break;
                        case authorsSyncKey:
                            authorsStore.reset();
                            await authorsStore.initialize();
                            break;
                        case categoriesSyncKey:
                            categoriesStore.reset();
                            await categoriesStore.initialize();
                            break;
                        case industrySectorsSyncKey:
                            industrySectorsStore.reset();
                            await industrySectorsStore.initialize();
                            break;
                        case productsSyncKey:
                            productsStore.reset();
                            await productsStore.initializeAsync();
                            break;
                        case sectorsSyncKey:
                            sectorsStore.reset();
                            await sectorsStore.initializeAsync();
                            break;
                        case tagsSyncKey:
                            tagsStore.reset();
                            await tagsStore.initialize();
                            break;
                        case tickersSyncKey:
                            tickersStore.reset();
                            await tickersStore.initialize();
                            break;
                        case simplePagesSyncKey:
                            simplePagesStore.reset();
                            await simplePagesStore.initializeAsync();
                            break;
                        case sectorAlertsKey:
                            sectorHistoryStore.reset();
                            await sectorHistoryStore.initializeAsync();
                            break;
                    }
                }
            } else if ( MessageTypes.LOAD_COMPLETE === messageType ) {
                // After the timeout expires, check for new content again
                setTimeout( fireDataSyncEvent, MaintenanceRefreshInterval );
            } else {
                await handleErrorsAsync( messageType, payload );
            }
        }
    }

    async function handleErrorsAsync( messageType: MessageTypes, payload?: string | undefined ): Promise< void > {
        if ( MessageTypes.UNAUTHORIZED === messageType ) {
            await resetApplication(true);
            await routeManager.handleForwardToSignInAndReturnAsync( router.currentRoute.value.path, router );
        } else if ( MessageTypes.FORBIDDEN === messageType ) {
            logger.error( `Received forbidden error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.INTERNAL_ERROR === messageType ) {
            logger.error( `Received internal error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.NOT_FOUND === messageType ) {
            logger.error( `Received not found error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.CONFIGURATION === messageType ) {
            logger.error( `Received a configuration error: ${ JSON.stringify( payload ) }` );
        } else if ( MessageTypes.UNKNOWN === messageType ) {
            logger.error( `Received an unknown error: ${ JSON.stringify( payload ) }` );
        }
    }

    function isAdminSession(): boolean {
        return adminSession.value;
    }

    async function resetApplication( logout?: boolean, doNotNavigate?: boolean ) {
        if ( !!logout ) {
            // Remove any authentication data that is stored in localstorage
            await authStore.logout();
        }

        // Clear application level service state
        adminSession.value = false;
        authenticating.value = false;
        initializing.value = false;
        publicInitialized.value = false;
        privateInitialized.value = false;
        loading.value = false;

        // Clear the current state of the UserStore
        articlesStore.reset();
        authorsStore.reset();
        bonusReportsStore.reset();
        categoriesStore.reset();
        coursesStore.reset();
        industrySectorsStore.reset();
        modelPortfoliosStore.reset();
        myPortfoliosStore.reset();
        permissionsStore.reset();
        productsStore.reset();
        sectorHistoryStore.reset();
        sectorsStore.reset();
        securitiesStore.reset();
        simplePagesStore.reset();
        tagsStore.reset();
        tickersStore.reset();
        tradeAlertsStore.reset();
        webinarsStore.reset();
        userStore.reset();

        if ( !!logout && !doNotNavigate ) {
            routeManager.handleRedirectWithOptionalExplicitPath( router, routeManager.homePath() );
        }
    }

    async function logoutAndGoHome(): Promise< void > {
        // If the user is logging out of the application, clear everything from active memory
        await resetApplication( true );

        // Forward the new session to the default landing page
        const currentPath: RouteLocationNormalizedLoaded | undefined = router?.currentRoute.value;
        if ( currentPath?.fullPath !== routeManager.homePath() ) {
            await router.push( routeManager.homePath() );
        } else {
            // refresh the current page
            router.go( 0 );
        }
    }

    async function nukeItFromOrbitItsTheOnlyWayToBeSure(): Promise< void > {
        // If the user is logging out of the application, clear everything from active memory
        await resetApplication( true );
        localStorage.clear();

        await resetCurrentCachedData();

        // Forward the new session to the default landing page
        const currentPath: RouteLocationNormalizedLoaded | undefined = router?.currentRoute.value;
        if ( currentPath?.fullPath !== routeManager.homePath() ) {
            await router.push( routeManager.homePath() );
        } else {
            // refresh the current page
            router.go( 0 );
        }
    }

    function terminateWorker() {
        if ( !!worker ) {
            worker.terminate();
        }
    }

    function handleRejectedPromise( event: PromiseRejectionEvent ): null {
        const rejectionReason = event.reason;
        logger.warn( `Unhandled Promise Rejection: REASON: ${ JSON.stringify( rejectionReason ) }` );

        // Make sure the browser is aware that the promise rejection was "handled"
        return null;
    }

    async function refreshFileCache() : Promise<void>
    {
        // Clear all local cached artifacts - images, html & js files
        if ( 'caches' in window ) {
            caches.keys().then( ( names ) => {
                names.forEach( async ( name ) => {
                    await caches.delete( name );
                } )
            } );

            // reload from the CDN
            location.reload();
        }
    }

    async function refreshVersion(): Promise< void > {
        const newVersionExists = await isNewVersionAvailable();

        if ( newVersionExists ) {
            refreshFileCache().then();
        }
    }

    return {
        // STATE
        apiStatus,
        compositionCtx,
        contentModalVisible,
        contentItem,
        contentKey,
        dateOfMostRecentMarketDataAvailable,

        // GETTERS
        isForbidden,
        isInternalError,
        isLoading,
        isNotFound,

        // ACTIONS
        authenticateWithToken,
        clearSelectedContent,
        handleRejectedPromise,
        initializeAsync,
        isAdminSession,
        logoutAndGoHome,
        nukeItFromOrbitItsTheOnlyWayToBeSure,
        resetApplication,
        setSelectedArticle,
        setSelectedBonusReport,
        setSelectedTradeAlert,
        setSelectedWebinar,
        signIn,
        signUp,
        terminateWorker,
        refreshVersion
    };
} );

if ( import.meta.hot ) {
    import.meta.hot.accept( acceptHMRUpdate( useApplicationStore, import.meta.hot ) );
}
